import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const Section8 = () => {
  const data = {
    title: 'Section 8 Company Registration',
    description: 'Register your NGO or non-profit organization under Section 8 of Companies Act 2013. Ideal for charitable, educational, scientific, and social welfare activities.',
    keywords: 'Section 8 company, NGO registration, non-profit company, charitable organization, 80G registration, 12A registration, FCRA',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          A Section 8 Company is a non-profit organization formed for promoting commerce, art, science, sports, education, 
          research, social welfare, religion, charity, protection of environment, or any other useful object. Unlike regular 
          companies, Section 8 Companies cannot distribute profits to members and must apply all income towards their objectives.
        </p>
        <p>
          <strong>Why Choose Section 8 Company?</strong> Section 8 Company is the most credible structure for non-profit 
          organizations in India. It provides limited liability protection to members, has better governance standards, 
          and is more trusted by donors, government agencies, and international organizations compared to Trust or Society. 
          Many CSR funds and foreign donations prefer Section 8 Companies.
        </p>
        <p>
          <strong>Legal Framework:</strong> Section 8 Companies are governed by Section 8 of Companies Act 2013 (previously 
          Section 25 of Companies Act 1956) and regulated by the Ministry of Corporate Affairs. They must obtain a license 
          from the Central Government before incorporation. The license confirms that the company will use profits only for 
          its objectives and will not pay dividends.
        </p>
        <p>
          <strong>Tax Exemptions:</strong> Section 8 Companies can apply for 12A registration (income tax exemption) and 
          80G registration (donors get tax deduction). With FCRA registration, they can receive foreign contributions. 
          These registrations significantly enhance fundraising capabilities and donor confidence.
        </p>
      </div>
    ),
    
    benefits: [
      'Most credible non-profit structure recognized by government and donors',
      'Limited liability protection for members and directors',
      'Separate legal entity with perpetual succession',
      'Tax exemption on income with 12A registration',
      'Donors get tax benefits with 80G registration',
      'Can receive foreign funds with FCRA registration',
      'Preferred by CSR departments of corporates',
      'Better governance and transparency standards',
      'No minimum capital requirement',
      'Can own property and enter contracts in company name',
      'Professional image enhances credibility for fundraising',
      'Easier to get grants from government and international agencies',
    ],
    
    documents: [
      'PAN and Aadhaar of all directors (minimum 2 directors required)',
      'Passport (for foreign nationals) with valid Visa',
      'Passport size photographs (recent, colored - 2 copies per director)',
      'Address proof of registered office: Rent agreement or Ownership documents',
      'Utility bill of registered office (electricity/water bill)',
      'NOC (No Objection Certificate) from property owner',
      'Declaration by directors in prescribed format',
      'Digital Signature Certificate (DSC) of directors',
      'Detailed objects clause specifying charitable activities',
      'Estimated income and expenditure for 3 years',
      'Declaration that profits will not be distributed',
      'List of proposed activities and target beneficiaries',
    ],
    
    process: [
      'Obtain Digital Signature Certificate (DSC) for directors – 1-2 days',
      'Apply for Director Identification Number (DIN) – 1 day',
      'Name reservation with charitable object indicator – 2-3 days',
      'Drafting of MOA with objects clause and non-profit declaration',
      'Drafting of AOA with governance rules',
      'Application for Section 8 License (Form INC-12) to Regional Director',
      'License approval by Regional Director – 15-20 days',
      'Filing of SPICe+ form for incorporation with license',
      'Certificate of Incorporation issued by ROC',
      'Apply for PAN and TAN for the company',
    ],
    
    timeline: '15-20 Working Days',
    pricing: '₹8,999 + Govt Fees',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Post-Incorporation Registrations:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>12A Registration:</strong> Exempts company income from tax – apply within 3 months of incorporation</li>
          <li><strong>80G Registration:</strong> Enables donors to claim tax deduction – apply after 12A</li>
          <li><strong>FCRA Registration:</strong> Required to receive foreign donations – apply after 3 years of operation</li>
          <li><strong>CSR-1 Registration:</strong> Required to receive CSR funds from corporates</li>
        </ul>
        <p className="mt-3"><strong>Annual Compliance:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Annual ROC filing (Form AOC-4 and MGT-7) within 30/60 days of AGM</li>
          <li>Annual General Meeting within 6 months of financial year end</li>
          <li>Board meetings – minimum 4 per year (one every quarter)</li>
          <li>Income Tax Return (ITR-7) by 31st October</li>
          <li>FCRA annual return (if registered) by 31st December</li>
          <li>Activity report to ROC if required</li>
        </ul>
        <p className="mt-3"><strong>Prohibited Activities:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Cannot distribute dividends or profits to members</li>
          <li>Cannot pay remuneration to directors beyond reasonable limits</li>
          <li>Cannot use funds for non-charitable purposes</li>
          <li>Cannot change objects without government approval</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'What is a Section 8 Company?',
        answer: 'Section 8 Company is formed for charitable purposes like education, sports, arts, science, religion, charity, social welfare, or environmental protection. It operates without profit motive and cannot distribute dividends. All surplus must be used for its objectives.',
      },
      {
        question: 'What is the minimum requirement for Section 8 Company?',
        answer: 'Minimum 2 directors (at least 1 Indian resident) and 2 subscribers are required. There is no minimum capital requirement. Directors can be subscribers. At least one director must have stayed in India for 120+ days in the previous calendar year.',
      },
      {
        question: 'Can Section 8 Company pay salaries to directors?',
        answer: 'Yes, reasonable remuneration can be paid to directors and employees for services rendered. However, this cannot be disguised profit distribution. Remuneration should be commensurate with the work done and within industry norms for non-profits.',
      },
      {
        question: 'What is the difference between Section 8 Company and Trust?',
        answer: 'Section 8 Company is governed by Companies Act with better governance and compliance. Trust is governed by Indian Trusts Act with simpler registration. Section 8 is preferred for larger operations, CSR funds, and foreign donations. Trust is simpler for local charitable activities.',
      },
      {
        question: 'How to get 80G and 12A registration?',
        answer: '12A registration exempts company income from tax. 80G registration enables donors to claim 50% or 100% tax deduction. Both are applied through Income Tax portal after incorporation. 12A should be applied first within 3 months, then 80G.',
      },
      {
        question: 'Can Section 8 Company receive foreign donations?',
        answer: 'Yes, but FCRA (Foreign Contribution Regulation Act) registration is required from Ministry of Home Affairs. FCRA can be applied after 3 years of operations with proven track record. Prior permission can be obtained for specific projects.',
      },
      {
        question: 'What happens if Section 8 Company makes profit?',
        answer: 'Section 8 Company can make surplus/profit from its activities, but it cannot distribute this to members as dividend. All surplus must be reinvested in the company\'s charitable objectives. Accumulated reserves can be used for expansion of activities.',
      },
      {
        question: 'Can Section 8 Company be converted to regular company?',
        answer: 'Yes, Section 8 Company can be converted to a regular Private Limited or Public Limited Company with approval from the Central Government. Upon conversion, it loses all tax exemptions and the assets must be transferred to another Section 8 Company.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default Section8;
